import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './AggregateResequenceView.module.css';
import SequenceToolbar from '../../components/SequenceToolbar/SequenceToolbar';
import moment from 'moment';
import { BackButton } from '..';
import { useHistory, useParams } from 'react-router-dom';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { getAggregateSequenceUrl } from '../../utils/utils';
import {
  API_RESOURCE_URLS,
  DATE_FORMAT,
  RESOURCE_TYPE,
  USER_OPERATIONS,
} from '../../constants';
import {
  buildErrorMessage,
  fetchAggregateSequenceForDatePlantAndLine,
} from '../../apis/calls';
import { withAllowedOperationsProvider } from '../../hocs';
import AggregateResequence from '../AggregateResequence/AggregateResequence';
import { API } from '../../apis/api';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { processAggregateSequenceData } from '../../utils/tableUtils';

function AggregateResequenceView({ aggregate }) {
  const [aggregateLine] = usePlantAndLinesStore().aggregateLineState;
  const [plant] = usePlantAndLinesStore().plantState;
  const { sequenceDate } = useParams();
  const [rowsData, setRowsData] = useState([]);
  const history = useHistory();
  const { showError, showInternalError } = usePopupManager();
  const fixedColumns = [
    {
      width: 140,
      title: 'Part No.',
      field: 'materialNumber',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 200,
      title: 'Part Description',
      field: 'materialDescription',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 235,
      title: 'ASN',
      field: 'parentAsn',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 235,
      title: 'Destination',
      field: 'destination',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 235,
      title: 'Chassis Line',
      field: 'parentLineName',
      enableSearch: true,
      enableFilter: true,
    },
  ];

  const redirectToAggregateSequencePage = () => {
    history.push(`${getAggregateSequenceUrl(aggregate)}/${sequenceDate}?preview=true`);
  };

  const loadAggregateSequenceDataForDate = async date => {
    const dateString = moment(date).format(DATE_FORMAT.ISO);
    try {
      const response = await fetchAggregateSequenceForDatePlantAndLine(
        dateString,
        plant.value,
        aggregateLine.value,
      );
      const processedRows = processAggregateSequenceData(response.data);

      setRowsData(processedRows);
    } catch (err) {
      console.error('failed to load sequence');
      setRowsData([]);
    }
  };
  const updateSequenceOrder = (date, destinationRows) => {
    try {
      return API.patch(
        API_RESOURCE_URLS.updateAggregateSequence(
          plant.value,
          aggregateLine.value,
        ),
        {
          date: moment(date).format(DATE_FORMAT.ISO),
          updatedSequence: destinationRows.map((row, index) => {
            return {
              sequence: index + 1,
              id: row.id,
            };
          }),
        },
      );
    } catch (error) {
      console.error(buildErrorMessage(error));
      showError({
        contextText: `Failed to Carry Forward ASN`,
        error: error,
      });
    }
  };
  const handlePreview = destinationRows => {
    updateSequenceOrder(sequenceDate, destinationRows)
      .then(() => {
        redirectToAggregateSequencePage();
      })
      .catch(error => {
        console.error('Error saving sequence', error);
        showInternalError();
      });
  };
  useEffect(() => {
    sequenceDate && loadAggregateSequenceDataForDate(sequenceDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequenceDate, aggregateLine, plant]);

  return (
    <>
      <div className={styles.container}>
        <BackButton
          basePath={aggregate}
          action="Sequence"
          handleClick={redirectToAggregateSequencePage}
        />
        <SequenceToolbar date={sequenceDate} lineName={aggregateLine.label} />
        <AggregateResequence
          rows={rowsData}
          columns={fixedColumns}
          defaultColumns={fixedColumns}
          primaryAction={{
            name: 'Preview',
            actionFn: destinationRows => {
              handlePreview(destinationRows);
            },
            authOperation: USER_OPERATIONS.UPDATE_AGGREGATE_PLAN_SEQUENCE,
            shouldEnable: destinationRows =>
              destinationRows.length === rowsData.length,
          }}
        />
      </div>
    </>
  );
}

AggregateResequenceView.propTypes = {
  aggregate: PropTypes.string.isRequired,
  primaryAction: PropTypes.shape({
    name: PropTypes.string.isRequired,
    actionFn: PropTypes.func.isRequired,
  }),
  secondaryActions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      actionFn: PropTypes.func.isRequired,
      backgroundColor: PropTypes.string,
    }),
  ),
};

export default withAllowedOperationsProvider(
  AggregateResequenceView,
  true,
  RESOURCE_TYPE.MATERIAL,
);
